<script>
import { useAlert } from 'dashboard/composables';
import { mapActions } from 'vuex';
import SectionCard from '../SectionCard.vue';
import RealEstateIcon from '../RealEstateIcon.vue';
import ImageUpload from '../components/ImageUpload.vue';
import { FIELD_CLASS } from '../constants';
import {
  apiErrorMessage,
  iconErrorMessage,
} from '../../../../api/realEstate/errors';

// No modal — features are added/removed inline, matching the prototype.
// A feature's icon is a separate upload (PUT .../features/{id}/icon), so
// it can only be set once the feature already exists — the add form only
// takes a name.
export default {
  name: 'FeaturesSection',
  components: { SectionCard, RealEstateIcon, ImageUpload },
  props: {
    project: {
      type: Object,
      required: true,
    },
  },
  data() {
    return {
      fieldClass: FIELD_CLASS,
      newName: '',
    };
  },
  methods: {
    ...mapActions('realEstate', [
      'addFeature',
      'removeFeature',
      'setFeatureIconFile',
      'removeFeatureIcon',
    ]),
    async handleAdd() {
      const name = this.newName.trim();
      if (!name) return;
      try {
        await this.addFeature({ propertyId: this.project.property_id, name });
        this.newName = '';
        useAlert(this.$t('REAL_ESTATE.FEATURES.SUCCESS_ADDED'));
      } catch (err) {
        useAlert(apiErrorMessage(this, err, 'REAL_ESTATE.FEATURES.ERROR_ADD'));
        // eslint-disable-next-line no-console
        console.error(err);
      }
    },
    async handleRemove(feature) {
      try {
        await this.removeFeature({
          propertyId: this.project.property_id,
          featureId: feature.amenity_id,
        });
        useAlert(this.$t('REAL_ESTATE.FEATURES.SUCCESS_REMOVED'));
      } catch (err) {
        useAlert(
          apiErrorMessage(this, err, 'REAL_ESTATE.FEATURES.ERROR_REMOVE')
        );
        // eslint-disable-next-line no-console
        console.error(err);
      }
    },
    async handleIconSelect(feature, file) {
      try {
        await this.setFeatureIconFile({
          propertyId: this.project.property_id,
          featureId: feature.amenity_id,
          file,
        });
        useAlert(this.$t('REAL_ESTATE.FEATURES.SUCCESS_ICON_UPDATED'));
      } catch (err) {
        useAlert(iconErrorMessage(this, err));
        // eslint-disable-next-line no-console
        console.error(err);
      }
    },
    async handleIconRemove(feature) {
      try {
        await this.removeFeatureIcon({
          propertyId: this.project.property_id,
          featureId: feature.amenity_id,
        });
        useAlert(this.$t('REAL_ESTATE.FEATURES.SUCCESS_ICON_REMOVED'));
      } catch (err) {
        useAlert(apiErrorMessage(this, err));
        // eslint-disable-next-line no-console
        console.error(err);
      }
    },
  },
};
</script>

<template>
  <SectionCard section-id="features" :title="$t('REAL_ESTATE.FEATURES.TITLE')">
    <ul v-if="project.features.length" class="space-y-3 mb-4">
      <li
        v-for="feature in project.features"
        :key="feature.amenity_id"
        class="flex items-center gap-3 text-sm text-slate-700 dark:text-slate-200"
      >
        <ImageUpload
          compact
          allow-svg
          :preview-url="feature.icon_url || ''"
          :label="$t('REAL_ESTATE.FEATURES.ICON_BUTTON')"
          @select="file => handleIconSelect(feature, file)"
          @remove="handleIconRemove(feature)"
        />
        <span class="flex-1 flex items-center gap-2">
          <RealEstateIcon
            v-if="!feature.icon_url"
            icon="star"
            :size="15"
            class="text-woot-500"
          />
          {{ feature.name }}
        </span>
        <woot-button
          variant="clear"
          size="small"
          color-scheme="alert"
          icon="delete"
          @click="handleRemove(feature)"
        />
      </li>
    </ul>
    <p v-else class="text-sm text-slate-400 mb-4">
      {{ $t('REAL_ESTATE.FEATURES.EMPTY') }}
    </p>

    <form
      class="flex flex-wrap items-center gap-2 pt-3 border-t border-slate-200 dark:border-slate-700"
      @submit.prevent="handleAdd"
    >
      <input
        v-model="newName"
        :placeholder="$t('REAL_ESTATE.FEATURES.FIELD_TITLE_PLACEHOLDER')"
        :class="fieldClass"
        class="flex-1 min-w-[160px] mb-0 reset-base"
        required
      />
      <woot-button type="submit" variant="smooth" class="shrink-0">
        {{ $t('REAL_ESTATE.FEATURES.ADD_BUTTON') }}
      </woot-button>
    </form>
  </SectionCard>
</template>
